import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, BookOpen, RefreshCw } from 'lucide-react';
import { usePortfolioData } from '../utils/usePortfolioData';

const Experience: React.FC = () => {
  const { profile, loading, error, refreshData } = usePortfolioData();

  if (loading) {
    return (
      <section id="experience" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <RefreshCw size={32} className="text-cyber-blue" />
              </motion.div>
              <p className="text-gray-400">Loading portfolio data...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="experience" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-red-400 mb-4">Error loading portfolio data: {error}</p>
            <button
              onClick={refreshData}
              className="bg-cyber-blue text-white px-4 py-2 rounded-lg hover:bg-cyber-purple transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!profile || !profile.experience || profile.experience.length === 0) {
    return (
      <section id="experience" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-4">
              Experience
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              No experience data available at the moment
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  const getIconForExperience = (title: string) => {
    if (title.toLowerCase().includes('founder') || title.toLowerCase().includes('member')) return Users;
    if (title.toLowerCase().includes('organizer') || title.toLowerCase().includes('event')) return Award;
    if (title.toLowerCase().includes('learning') || title.toLowerCase().includes('challenge')) return BookOpen;
    return Users;
  };

  const getColorForExperience = (index: number) => {
    const colors = [
      'from-cyber-blue to-cyber-purple',
      'from-cyber-purple to-cyber-neon',
      'from-cyber-neon to-cyber-blue',
      'from-cyber-blue to-cyber-purple'
    ];
    return colors[index % colors.length];
  };

  return (
    <section id="experience" className="section-padding bg-cyber-darker">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-6">
            Experience
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            My journey in cybersecurity and community building
          </p>
        </motion.div>

        <div className="space-y-12">
          {profile.experience.map((exp, index) => {
            const Icon = getIconForExperience(exp.title);
            const gradientClass = getColorForExperience(index);
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row gap-8 items-start ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Icon and Timeline */}
                <div className="flex flex-col items-center lg:items-start space-y-4 lg:w-1/4">
                  <div className={`p-4 rounded-full bg-gradient-to-r ${gradientClass} shadow-lg`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <div className="text-center lg:text-left">
                    <p className="text-cyber-blue font-mono text-sm">{exp.duration}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div className="bg-cyber-dark rounded-lg p-8 border border-cyber-blue/20 hover:border-cyber-blue/40 transition-all duration-300">
                    <h3 className="text-xl font-bold text-white mb-2">{exp.title}</h3>
                    <p className="text-cyber-blue font-medium mb-4">{exp.company}</p>
                    <p className="text-gray-300 leading-relaxed text-lg">{exp.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience; 